// Parts catalog browser. Replaces the legacy /parts ffpartnum list.
//
// Existing rows are editable for metadata only (PATCH /api/parts-catalog/:id).
// The `sku` field is immutable post-create per the SKU naming convention and
// the backend rejects it in the patch payload.
//
// "New item" (item-first mint, ERP expansion chunk 1) creates the catalog row
// ONLY — no parts_inventory rows. A minted SKU shows zero on hand until the
// first box of it is actually received; see PartsCatalogService.mint.
//
// Two modes on one table: the catalog, and bulk editing (?mode=planning, opened
// from Tools by editors only), where reorder threshold, safety stock and lead
// time are typed in place for dozens of parts at a time. The editable cells carry their drafts and save
// statuses through the DataTable `meta` prop, so the ONE columns array below
// never changes identity and a cell never remounts under the caret.
//
// Filters: q (sku/description/mpn ilike), family, kind_key, applicable_to, is_consumable,
// tracked_per_instance, auto_created_since (Recently auto-created last 7d),
// include_inactive (Show retired — the ONE list that can see retired items,
// because it is the item master and the way back to one).
// All server-side here (unlike the inventory page, whose chips filter
// client-side — a deliberate split, each pinned by its own tests).
//
// The table is the shared DataTable (components/table): sorting, group rows
// and capability-driven column visibility live there.
//
// ── NAME FIRST, AND THE ONE PLACE THE CODE COLUMN STAYS ─────────────────────
// Everywhere else in the app an item is its NAME and the SKU is behind the
// scenes. This page is the exception, because it is the item MASTER: the list
// somebody opens precisely to find, compare or correct a code. So the item
// leads and carries the link, and the SKU keeps a mono column of its own
// immediately after it.
//
// The description column is gone. Its content is the name and the variant
// detail now, and a third column repeating them was the widest thing here.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Link, useHistory, useLocation } from 'react-router-dom';
import type { ColumnDef, Table } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import PageHeader, { PrimaryAction, SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { type MenuItem } from '../components/ui/Menu';
import {
  FilterBar,
  FilterBreak,
  FilterSearch,
  FilterSelect,
} from '../components/FilterBar';
import Chip from '../components/ui/Chip';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import useItemKindsStore, { shortKinds, structuredKinds } from '../stores/useItemKindsStore';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/ui/EmptyState';
import { Progress } from '../components/ui/Progress';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { DataTable, type DataTableHandle } from '../components/table/DataTable';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { Tag } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import type {
  PartsCatalogRow,
  PartFamilyRow,
  MaterialType,
  CommodityClass,
} from '../Receiving/types';
import ItemName, { itemLabel } from '../components/ItemName';
import NewItemModal from './NewItemModal';
import UnmintItemDialog from './UnmintItemDialog';
import { bomPath } from './bomsApi';
import {
  ClassificationCell,
  CommodityClassFilterChips,
  MaterialTypeFilterSelect,
  MATERIAL_TYPES,
  MATERIAL_TYPE_META,
  COMMODITY_CLASSES,
  COMMODITY_CLASS_META,
} from './classificationDisplay';

const formatCurrency = (n: string | null) =>
  n === null || n === undefined ? '—' : `$${parseFloat(n).toFixed(2)}`;

const sevenDaysAgoIso = () => {
  const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return d.toISOString();
};

const getRowId = (r: PartsCatalogRow) => String(r.id);

/** The server's own limit on parts_catalog.name. Mirrored here so the field
 *  stops at 60 rather than letting somebody type a sentence and be told no. */
const NAME_MAX = 60;

/**
 * The item cell: the name, the variant detail under the same phrase, and a
 * rename that happens right here.
 *
 * MODULE SCOPE, not a closure inside the columns memo — flexRender treats a
 * function as a component TYPE, so a fresh identity per render would remount
 * the cell and close the input mid-word.
 *
 * The rename affordance is revealed by hover OR focus-within, which keeps 500
 * rows from carrying 500 permanent edit buttons while leaving the control in
 * the tab order: it is reachable by keyboard alone, and the item page carries
 * the same field for anyone who never finds it here.
 */
const ItemCell: React.FC<{
  row: PartsCatalogRow;
  canEdit: boolean;
  onRename: (row: PartsCatalogRow, name: string | null) => void;
}> = ({ row, canEdit, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Whether a blur still means "save". Escape and Enter both close the field,
  // and removing a focused element fires blur on the way out — without this,
  // Escape saved what it was cancelling and Enter patched the row twice.
  const blurSaves = useRef(true);

  const stop = (save: boolean) => {
    blurSaves.current = false;
    setEditing(false);
    if (!save) return;
    const next = draft.trim();
    // Empty means "no name of its own", which is a real answer: the server
    // falls back to the generated one. It is not the same as cancelling.
    const value = next ? next : null;
    if (value !== (row.name || null)) onRename(row, value);
  };

  if (editing) {
    return (
      <Field
        variant="inline"
        autoFocus
        value={draft}
        maxLength={NAME_MAX}
        aria-label={`Name, ${itemLabel(row)}`}
        placeholder="What do people call this?"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            // Swallowed only because this field used it. Anything above wants
            // Escape when nothing is being edited.
            e.stopPropagation();
            stop(false);
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            stop(true);
          }
        }}
        onBlur={() => { if (blurSaves.current) stop(true); }}
        className="w-full"
      />
    );
  }

  return (
    <div className="group flex items-baseline gap-1.5 min-w-0">
      <Link to={`/parts-catalog/${row.id}`} className="min-w-0 hover:underline">
        <ItemName item={row} size="sm" />
      </Link>
      {/* Only ever seen with "Show retired" on, because the server does not
          send these otherwise. Outside the link and outside the export: the
          column's CSV value is `itemLabel`, and a state is not part of a name. */}
      {!row.is_active && (
        <Tag size="sm" className="shrink-0">Retired</Tag>
      )}
      {row.model_code && (
        <Link
          to={bomPath(row.model_code)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open the ${row.model_code} parts list`}
          className="shrink-0 rounded-full bg-surface-container-highest px-1.5 py-0.5 text-[10px] text-primary hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
        >
          Model
        </Link>
      )}
      {canEdit && (
        <button
          type="button"
          onClick={() => {
            blurSaves.current = true;
            setDraft(row.name || '');
            setEditing(true);
          }}
          aria-label={`Rename ${itemLabel(row)}`}
          className="shrink-0 text-[10px] uppercase tracking-wider text-on-surface-variant opacity-0
                     transition-opacity group-hover:opacity-100 group-focus-within:opacity-100
                     hover:text-primary focus:outline-none focus-visible:opacity-100
                     focus-visible:ring-1 focus-visible:ring-primary/50 rounded px-0.5"
        >
          rename
        </button>
      )}
    </div>
  );
};

// ── REORDER LEVELS MODE ─────────────────────────────────────────────────────

type PageMode = 'catalog' | 'planning';
type PlanningField = 'reorder_threshold' | 'safety_stock' | 'lead_time_days';

type CellStatus = { kind: 'saving' } | { kind: 'saved'; seq: number } | { kind: 'error'; message: string };

/** What the editable cells read out of the table at render time. A fresh object
 *  every render, on purpose: see "Volatile state travels by meta" in
 *  docs/features/shared-table.md. */
interface CatalogMeta {
  planning: boolean;
  /** Typed text not yet saved, keyed `${rowId}:${field}`. */
  drafts: Record<string, string>;
  statuses: Record<string, CellStatus>;
  onDraft: (key: string, text: string | undefined) => void;
  /** Returns false only when the text is invalid, so Enter knows to stay put. */
  onSave: (rowId: number, field: PlanningField, text: string) => boolean;
}

const PLANNING_FIELDS: Record<PlanningField, { label: string; placeholder: string }> = {
  reorder_threshold: { label: 'Reorder threshold', placeholder: 'none' },
  safety_stock: { label: 'Safety stock', placeholder: 'none' },
  // Null here means nobody has asked the vendor. It is not zero.
  lead_time_days: { label: 'Lead time in days', placeholder: 'not asked' },
};

const LEAD_TIME_MAX = 3650;

type Parsed = { ok: true; value: number | null } | { ok: false; message: string };

const parsePlanning = (text: string, field: PlanningField): Parsed => {
  const t = text.trim();
  if (t === '') return { ok: true, value: null };
  if (!/^\d+$/.test(t) || !Number.isSafeInteger(Number(t))) {
    return { ok: false, message: 'Use a whole number, 0 or more.' };
  }
  const n = Number(t);
  if (field === 'lead_time_days' && n > LEAD_TIME_MAX) {
    return { ok: false, message: `Lead time is at most ${LEAD_TIME_MAX} days.` };
  }
  return { ok: true, value: n };
};

const fieldText = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

const readMeta = (table: Table<PartsCatalogRow>) => table.options.meta as CatalogMeta;

/** Enter moves down the column, Shift+Enter up, in rendered order (so sorting
 *  and group rows are respected without the page knowing either). */
const focusNeighbour = (from: HTMLInputElement, field: PlanningField, step: 1 | -1) => {
  const all = Array.from(
    document.querySelectorAll<HTMLInputElement>(`[data-planning-field="${field}"]`)
  );
  const next = all[all.indexOf(from) + step];
  if (next) {
    next.focus();
    next.select();
  }
};

const WorkingMark: React.FC = () => <LogoSpinner size="inline" label="Saving" />;

/** The surface's one moment of charm: a check lands beside the field and fades.
 *  Under reduced motion it neither scales in nor fades, it just goes. */
const SavedMark: React.FC = () => {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setFading(true), 1200);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={`h-3.5 w-3.5 text-green-400 transition-opacity duration-300 ease-emphasized motion-safe:animate-menuIn motion-reduce:transition-none ${
        fading ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
};

/**
 * One editable planning number. MODULE SCOPE and not React.memo: it reads the
 * table's meta, and a memoized cell would skip the render that carries a new
 * draft or status.
 *
 * Always an input, so the grid is keyboard-drivable without a click to open
 * anything. `type="text"` with `inputMode="numeric"` rather than type=number:
 * a wheel scroll or a spinner click over a focused number input changes the
 * value, and in a dense grid that is somebody's threshold silently moving.
 */
const PlanningCell: React.FC<{
  row: PartsCatalogRow;
  field: PlanningField;
  meta: CatalogMeta;
}> = ({ row, field, meta }) => {
  const stored = row[field] ?? null;

  const key = `${row.id}:${field}`;
  const draft = meta.drafts[key];
  const status = meta.statuses[key];
  const errored = status?.kind === 'error';
  const errId = `planning-err-${row.id}-${field}`;
  // Nothing typed, nothing to save. This is also what keeps an Escape during a
  // save from re-sending the old value on the blur that follows.
  const commit = (el: HTMLInputElement) =>
    draft === undefined ? true : meta.onSave(row.id, field, el.value);

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center justify-end gap-1.5">
        {/* Reserved whether or not a mark is showing, so a save never shifts the field. */}
        <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center" aria-live="polite">
          <Appear show={status?.kind === 'saving'} className="absolute inset-0 flex items-center justify-center">
            <WorkingMark />
          </Appear>
          <Appear show={status?.kind === 'saved'} className="absolute inset-0 flex items-center justify-center">
            <SavedMark />
            <span className="sr-only">Saved</span>
          </Appear>
        </span>
        <Field
          size="sm"
          error={errored}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="next"
          data-planning-field={field}
          aria-label={`${PLANNING_FIELDS[field].label}, ${itemLabel(row)}`}
          aria-invalid={errored || undefined}
          aria-describedby={errored ? errId : undefined}
          placeholder={PLANNING_FIELDS[field].placeholder}
          value={draft !== undefined ? draft : fieldText(stored)}
          onChange={(e) => meta.onDraft(key, e.target.value)}
          onBlur={(e) => { commit(e.currentTarget); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (commit(e.currentTarget)) focusNeighbour(e.currentTarget, field, e.shiftKey ? -1 : 1);
            } else if (e.key === 'Escape' && draft !== undefined) {
              // Swallowed only because this field had something to cancel.
              e.stopPropagation();
              meta.onDraft(key, undefined);
            }
          }}
          className="m-0 !w-24 text-right tabular-nums"
        />
      </div>
      <Reveal show={status?.kind === 'error'}>
        <span id={errId} className="mt-1 block max-w-[11rem]">
          <Alert variant="text" className="text-[11px] leading-tight">{status?.kind === 'error' ? status.message : null}</Alert>
        </span>
      </Reveal>
    </div>
  );
};

const groupKey = (r: PartsCatalogRow) => r.family_label || r.family_key || '(no family)';

const PartsCatalogPage: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const location = useLocation();
  const history = useHistory();
  // Mirrors the backend gate on PATCH and POST /api/parts-catalog (procurement | admin).
  const canEdit = user.isadmin || user.can_admin || user.can_procurement;
  // URL state, replaced rather than pushed: flipping the view is not a place
  // somebody wants Back to return to. Editors only: for anyone else the mode is
  // a grid of inputs they cannot use, so the URL opens the plain catalog.
  const planning = canEdit && new URLSearchParams(location.search).get('mode') === 'planning';
  const changeMode = (next: PageMode) => {
    const params = new URLSearchParams(location.search);
    if (next === 'planning') params.set('mode', 'planning');
    else params.delete('mode');
    const qs = params.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' });
  };
  const isAdmin = user.isadmin || user.can_admin;
  // Gated the same today. Its own name because it answers a different question
  // (may this person add an item) and the two gates can part.
  const canMint = canEdit;
  // Strict: cost/price visibility is opt-in even for admins (see require-financial).
  const canSeeFinancials = !!user.can_financial;

  const [rows, setRows] = useState<PartsCatalogRow[]>([]);
  const [families, setFamilies] = useState<PartFamilyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  /** The row whose delete is being confirmed, or null. */
  const [unminting, setUnminting] = useState<PartsCatalogRow | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [familyId, setFamilyId] = useState<string>('');
  const [kindKey, setKindKey] = useState<string>('');
  const [consumable, setConsumable] = useState<'' | 'true' | 'false'>('');
  const [perInstance, setPerInstance] = useState<'' | 'true' | 'false'>('');
  const [recentOnly, setRecentOnly] = useState(false);
  // Retired items are absent from every list by default, this one included:
  // the catalog is the item MASTER, so it is also the one place with a way to
  // ask for them back.
  const [showRetired, setShowRetired] = useState(false);
  const [groupByFamily, setGroupByFamily] = useState(false);
  // ERP expansion mini-chunk: raw-materials separation. Same table, one more
  // server-side filter alongside is_consumable/tracked_per_instance above.
  const [commodityFilter, setCommodityFilter] = useState<'' | CommodityClass>('');
  const [materialFilter, setMaterialFilter] = useState<'' | MaterialType>('');
  // Export CSV writes what the table is holding, so it asks the table rather
  // than re-deriving the rows here.
  const tableRef = useRef<DataTableHandle>(null);

  const itemKinds = useItemKindsStore((s) => s.kinds);
  const loadKinds = useItemKindsStore((s) => s.load);
  const kindOptions = useMemo(
    () => [...structuredKinds(itemKinds), ...shortKinds(itemKinds)],
    [itemKinds]
  );

  // Reorder levels editing state. Drafts and statuses are keyed
  // `${rowId}:${field}` and reach the cells through the table's meta.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<Record<string, CellStatus>>({});
  const rowsRef = useRef<PartsCatalogRow[]>([]);
  // A key is present while its PATCH is in the air. `seq` lets a response know
  // whether a newer save for the same field has since replaced it.
  const inflightRef = useRef<Record<string, { seq: number; value: number | null }>>({});
  const seqRef = useRef(0);
  // The newest response merged per key. A success overtaken by a newer save
  // still lands in the row (the server took it) unless something newer already has.
  const appliedRef = useRef<Record<string, number>>({});
  // The text the server last refused (a 4xx), so a blur does not re-send it.
  // A network failure or a 500 is never recorded here: the same text may succeed.
  const failedRef = useRef<Record<string, string>>({});
  const savedTimers = useRef<number[]>([]);

  // One page of items; the server caps at 500. Export CSV names this cap in its
  // hint when the page is full.
  const CATALOG_PAGE_SIZE = 500;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: String(CATALOG_PAGE_SIZE) });
      if (search.trim()) params.set('q', search.trim());
      if (familyId) params.set('part_family_id', familyId);
      if (kindKey) params.set('kind_key', kindKey);
      // Only the planning view pays for the stock figures: the catalog list
      // also feeds pickers and keeps its default shape.
      if (planning) params.set('with_stock', 'true');
      if (consumable) params.set('is_consumable', consumable);
      if (perInstance) params.set('tracked_per_instance', perInstance);
      if (recentOnly) params.set('auto_created_since', sevenDaysAgoIso());
      // Absent, not `false`: the server's default is already active-only, and a
      // parameter that says what would have happened anyway is one more thing
      // to keep in step with it.
      if (showRetired) params.set('include_inactive', 'true');
      if (commodityFilter) params.set('commodity_class', commodityFilter);
      if (materialFilter) params.set('material_type', materialFilter);
      const data = (await api.get(`/parts-catalog?${params.toString()}`)) as {
        rows: PartsCatalogRow[];
      };
      setRows(data.rows || []);
      // A refetch is a new answer; typed-but-unsaved text and old marks belong to the old one.
      setDrafts({});
      setStatuses({});
      failedRef.current = {};
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load parts catalog');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, familyId, kindKey, planning, consumable, perInstance, recentOnly, showRetired, commodityFilter, materialFilter]);

  useEffect(() => {
    (async () => {
      try {
        const fams = (await api.get('/part-families')) as PartFamilyRow[];
        setFamilies(fams || []);
      } catch {
        // Not fatal — page renders without the family filter.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // GET /api/item-kinds is procurement | admin, the same people as canEdit;
    // anyone else would only collect a 403 and an empty filter.
    if (canEdit) loadKinds(api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timers = savedTimers.current;
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchRows(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchRows, search]);

  // Stable so the columns memo below never recomputes on render (the DataTable
  // contract: fresh column identities remount every cell, which would close an
  // open ClassificationCell mid-edit). api/toast are stable per house convention.
  const patchRow = useCallback(
    async (row: PartsCatalogRow, body: Partial<PartsCatalogRow>) => {
      try {
        const updated = (await api.patch(`/parts-catalog/${row.id}`, { body })) as PartsCatalogRow;
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
        toast.success(`Updated ${itemLabel({ ...row, ...updated })}`);
      } catch (err: any) {
        const code = err?.data?.error?.code;
        if (code === 'tracked_per_instance_admin_only') {
          toast.error('Only admins can flip per-instance tracking');
        } else {
          toast.error(err?.message || 'Update failed');
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  rowsRef.current = rows;

  const onDraft = useCallback((key: string, text: string | undefined) => {
    delete failedRef.current[key];
    setDrafts((prev) => {
      if (text === undefined) {
        if (!(key in prev)) return prev;
        const { [key]: _gone, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: text };
    });
    // Editing again withdraws the complaint about the old text.
    setStatuses((prev) => {
      if (prev[key]?.kind !== 'error') return prev;
      const { [key]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const setStatus = useCallback((key: string, status: CellStatus | undefined) => {
    setStatuses((prev) => {
      if (status === undefined) {
        if (!(key in prev)) return prev;
        const { [key]: _gone, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: status };
    });
  }, []);

  /**
   * Save one planning number. Returns false only for text that cannot be sent,
   * so the cell keeps focus on it; "nothing to do" and "already on its way"
   * both return true.
   *
   * Enter saves and then moves focus, and moving focus blurs this very input,
   * which asks to save the same text again. The in-flight entry is written
   * before the request goes out, so that second ask finds it and stands down
   * (the double-PATCH trap ItemCell documents).
   *
   * Stable on purpose, api/toast omitted: the columns hold it through meta and
   * a fresh identity per render would defeat the point of meta.
   */
  const savePlanning = useCallback(
    (rowId: number, field: PlanningField, text: string): boolean => {
      const key = `${rowId}:${field}`;
      if (failedRef.current[key] === text) return false;
      const parsed = parsePlanning(text, field);
      // `in`, not `!parsed.ok`: with strict off, a boolean discriminant does not narrow.
      if ('message' in parsed) {
        setStatus(key, { kind: 'error', message: parsed.message });
        return false;
      }
      const row = rowsRef.current.find((r) => r.id === rowId);
      if (!row) return true;
      const value = parsed.value;
      const inflight = inflightRef.current[key];
      if (inflight) {
        if (inflight.value === value) return true;
      } else if (value === (row[field] ?? null)) {
        // Back to what is already saved: nothing to send, so drop the draft.
        onDraft(key, undefined);
        setStatus(key, undefined);
        return true;
      }

      const seq = ++seqRef.current;
      inflightRef.current[key] = { seq, value };
      setStatus(key, { kind: 'saving' });
      (async () => {
        try {
          const updated = (await api.patch(`/parts-catalog/${rowId}`, {
            body: { [field]: value },
          })) as PartsCatalogRow;
          const latest = inflightRef.current[key]?.seq === seq;
          if (seq > (appliedRef.current[key] ?? 0)) {
            appliedRef.current[key] = seq;
            // Merge ONLY the saved field: two fields on one row can be saved back
            // to back, and a full-row merge from the earlier response arriving
            // late would put the later field's old value back. It also keeps
            // on_hand and on_order, which a PATCH response does not carry.
            setRows((prev) =>
              prev.map((r) =>
                r.id === rowId ? { ...r, [field]: updated[field], updated_at: updated.updated_at } : r
              )
            );
          }
          if (!latest) return;
          delete inflightRef.current[key];
          // Keep the draft if they have already typed something newer.
          setDrafts((prev) => {
            if (prev[key] !== text) return prev;
            const { [key]: _gone, ...rest } = prev;
            return rest;
          });
          setStatus(key, { kind: 'saved', seq });
          const t = window.setTimeout(() => {
            setStatuses((prev) => {
              const cur = prev[key];
              if (cur?.kind !== 'saved' || cur.seq !== seq) return prev;
              const { [key]: _gone, ...rest } = prev;
              return rest;
            });
          }, 1500);
          savedTimers.current.push(t);
        } catch (err: any) {
          if (inflightRef.current[key]?.seq !== seq) return;
          delete inflightRef.current[key];
          const refused = [400, 403, 409, 422].includes(err?.status);
          if (refused) failedRef.current[key] = text;
          const message = err?.data?.error?.message || err?.message || 'Could not save. Try again.';
          setStatus(key, { kind: 'error', message });
          // A refusal is already said in the cell. A toast is for the failure
          // nobody expected (no connection, a 500), and never one per save.
          if (!refused) toast.error(message);
        }
      })();
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Delete an item that never happened. Throws on refusal so the dialog can
   * render the per-table counts the 409 carries — swallowing it here would
   * leave the person with "no" and no reason.
   */
  const unmintRow = async (row: PartsCatalogRow) => {
    await api.delete(`/parts-catalog/${row.id}`);
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    setUnminting(null);
    toast.success(`Deleted ${itemLabel(row)}`);
  };

  // Splice the freshly minted row straight into the table — the response is
  // already the same shape GET /api/parts-catalog lists, so no refetch needed.
  // Re-sorted by the item name, which is what the table's default sort is on.
  const handleMinted = (row: PartsCatalogRow) => {
    setRows((prev) => [...prev, row].sort((a, b) => itemLabel(a).localeCompare(itemLabel(b))));
    toast.success(`Created ${itemLabel(row)}`);
  };

  const columns = useMemo<ColumnDef<PartsCatalogRow, any>[]>(
    () => [
      {
        // The item leads, and carries the link the SKU used to. Sorted on the
        // same string it prints, so what you read is what it ordered by.
        id: 'item',
        header: 'Item',
        accessorFn: (r: PartsCatalogRow) => itemLabel(r),
        meta: { cellClass: 'max-w-[22rem]' },
        cell: ({ row, table }) => (
          <ItemCell
            row={row.original}
            // Rename's hover button is one more tab stop per row, and Reorder
            // levels is a keyboard-driven grid. The item page keeps the field.
            canEdit={canEdit && !readMeta(table).planning}
            onRename={(r, name) => patchRow(r, { name })}
          />
        ),
      },
      {
        // Two steps down from the name and no longer a link: it is the code on
        // the label, not the way in. This is the only list that keeps it.
        id: 'sku',
        header: 'SKU',
        accessorFn: (r: PartsCatalogRow) => r.sku,
        meta: { cellClass: 'font-mono text-xs text-on-surface-variant whitespace-nowrap' },
        cell: ({ row }) => row.original.sku,
      },
      {
        id: 'mpn',
        header: 'MPN',
        accessorFn: (r: PartsCatalogRow) => r.mpn ?? undefined,
        meta: { cellClass: 'text-on-surface-variant' },
        // Manufacturer stacks UNDER the MPN rather than taking a column of its
        // own. They are one fact in two halves, the table is already wide, and
        // a bare MPN is ambiguous without the name above it.
        cell: ({ row }) => (
          <>
            {row.original.manufacturer ? (
              <span className="block text-[11px] text-on-surface-variant leading-tight">
                {row.original.manufacturer}
              </span>
            ) : null}
            <span className="block leading-tight">{row.original.mpn || '—'}</span>
          </>
        ),
      },
      // Both classification axes, in their own columns since 019. Item type
      // moved OUT of the description cell to sit beside build stage: they are
      // one question asked twice, and reading them a column apart made the
      // pair hard to see.
      {
        id: 'material_type',
        header: 'Build stage',
        // The badge's own label, off the same map the cell reads. The badge
        // lowercases it as a visual treatment; the file keeps the map's
        // spelling, which is how the axis is written everywhere else.
        meta: { csv: (r: PartsCatalogRow) => MATERIAL_TYPE_META[r.material_type]?.short ?? null },
        cell: ({ row }) => (
          <ClassificationCell
            value={row.original.material_type}
            options={MATERIAL_TYPES}
            meta={MATERIAL_TYPE_META}
            canEdit={canEdit}
            label="Build stage"
            rowLabel={itemLabel(row.original)}
            onChange={(next) => patchRow(row.original, { material_type: next })}
          />
        ),
      },
      {
        id: 'commodity_class',
        header: 'Item type',
        meta: { csv: (r: PartsCatalogRow) => COMMODITY_CLASS_META[r.commodity_class]?.short ?? null },
        cell: ({ row }) => (
          <ClassificationCell
            value={row.original.commodity_class}
            options={COMMODITY_CLASSES}
            meta={COMMODITY_CLASS_META}
            canEdit={canEdit}
            label="Item type"
            rowLabel={itemLabel(row.original)}
            onChange={(next) => patchRow(row.original, { commodity_class: next })}
          />
        ),
      },
      {
        id: 'family',
        header: 'Family',
        accessorFn: (r: PartsCatalogRow) => r.family_label ?? undefined,
        meta: { cellClass: 'text-on-surface-variant text-xs' },
        cell: ({ row }) => row.original.family_label || '—',
      },
      {
        id: 'is_consumable',
        header: 'Cons.',
        // The header is short because the column is narrow; a file has no such
        // constraint, and "Cons." in a spreadsheet names nothing.
        meta: { align: 'center', csv: (r: PartsCatalogRow) => r.is_consumable, csvHeader: 'Consumable' },
        cell: ({ row }) => (
          <Checkbox
            size="sm"
            checked={row.original.is_consumable}
            disabled={!canEdit}
            onChange={(e) => patchRow(row.original, { is_consumable: e.target.checked })}
            aria-label={`Toggle consumable on ${itemLabel(row.original)}`}
          />
        ),
      },
      {
        id: 'tracked_per_instance',
        header: 'Per-instance',
        meta: { align: 'center', csv: (r: PartsCatalogRow) => r.tracked_per_instance },
        cell: ({ row }) => (
          <Tooltip label={!isAdmin ? 'Admin-only: affects per-board QR sticker requirement' : ''}>
            <Checkbox
              size="sm"
              checked={row.original.tracked_per_instance}
              disabled={!isAdmin}
              onChange={(e) => patchRow(row.original, { tracked_per_instance: e.target.checked })}
              aria-label={`Toggle per-instance tracking on ${itemLabel(row.original)}${!isAdmin ? ' (admin only)' : ''}`}
            />
          </Tooltip>
        ),
      },
      {
        id: 'reorder',
        header: 'Reorder',
        accessorFn: (r: PartsCatalogRow) => r.reorder_threshold ?? undefined,
        meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
        cell: ({ row }) => row.original.reorder_threshold ?? '—',
      },
      // Reorder levels mode. Stock figures first, then the three numbers people
      // set. Hidden in the catalog by columnVisibility, never by a second columns
      // array: a fresh array would remount every cell.
      {
        id: 'on_hand',
        header: 'On hand',
        accessorFn: (r: PartsCatalogRow) => r.on_hand ?? undefined,
        meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
        cell: ({ row }) => row.original.on_hand ?? '—',
      },
      {
        id: 'on_order',
        header: 'On order',
        accessorFn: (r: PartsCatalogRow) => r.on_order ?? undefined,
        meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
        cell: ({ row }) => row.original.on_order ?? '—',
      },
      {
        id: 'reorder_at',
        header: 'Reorder at',
        accessorFn: (r: PartsCatalogRow) => r.reorder_threshold ?? undefined,
        meta: { align: 'right' },
        cell: ({ row, table }) => (
          <PlanningCell row={row.original} field="reorder_threshold" meta={readMeta(table)} />
        ),
      },
      {
        id: 'safety_stock',
        header: 'Safety stock',
        accessorFn: (r: PartsCatalogRow) => r.safety_stock ?? undefined,
        meta: { align: 'right' },
        cell: ({ row, table }) => (
          <PlanningCell row={row.original} field="safety_stock" meta={readMeta(table)} />
        ),
      },
      {
        id: 'lead_time_days',
        header: 'Lead time (days)',
        accessorFn: (r: PartsCatalogRow) => r.lead_time_days ?? undefined,
        meta: { align: 'right' },
        cell: ({ row, table }) => (
          <PlanningCell row={row.original} field="lead_time_days" meta={readMeta(table)} />
        ),
      },
      {
        id: 'unit_cost',
        header: 'Unit cost',
        accessorFn: (r: PartsCatalogRow) =>
          r.unit_cost === null ? undefined : parseFloat(r.unit_cost),
        meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
        cell: ({ row }) => formatCurrency(row.original.unit_cost),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: 'right' },
        // STRICTLY false, never falsy: an absent flag is "we did not ask", and
        // offering to delete on that basis is how the affordance ends up on a
        // row with four years of receiving behind it. Quiet by default — this
        // is a rare correction, not a second primary action on every row.
        cell: ({ row }) =>
          row.original.has_history === false ? (
            <button
              type="button"
              onClick={() => setUnminting(row.original)}
              className={buttonClass('danger-quiet', 'sm')}
              aria-label={`Delete ${itemLabel(row.original)}`}
            >
              Delete
            </button>
          ) : null,
      },
    ],
    [canEdit, isAdmin, patchRow]
  );

  const columnVisibility = useMemo(
    () =>
      planning
        ? {
            mpn: false,
            material_type: false,
            commodity_class: false,
            is_consumable: false,
            tracked_per_instance: false,
            reorder: false,
            unit_cost: false,
            actions: false,
            on_hand: true,
            on_order: true,
            reorder_at: true,
            safety_stock: true,
            lead_time_days: true,
          }
        : {
            unit_cost: canSeeFinancials,
            actions: canMint,
            on_hand: false,
            on_order: false,
            reorder_at: false,
            safety_stock: false,
            lead_time_days: false,
          },
    [planning, canSeeFinancials, canMint]
  );

  // A drone model never holds stock, so it never gets a threshold.
  const displayRows = useMemo(
    () => (planning ? rows.filter((r) => !r.model_code) : rows),
    [planning, rows]
  );
  const withLevel = useMemo(
    () => displayRows.filter((r) => r.reorder_threshold !== null).length,
    [displayRows]
  );

  const tableMeta: CatalogMeta = {
    planning,
    drafts,
    statuses,
    onDraft,
    onSave: savePlanning,
  };

  // Export's hint names what the file will hold before it is asked for: the
  // filters above are the whole difference between an export somebody wanted
  // and a dump of everything.
  // A full page says so in the hint ("the first 500 rows"): a file that leaves
  // the app must not read as everything when it is a page.
  const atCap = rows.length >= CATALOG_PAGE_SIZE;
  const exportCount = displayRows.length;
  const tools: MenuItem[] = [
    // Bulk editing is an occasional sitting, not a view people browse in, so it
    // lives in Tools and only for the people who can save what they type.
    ...(canEdit
      ? [
          {
            key: 'bulk-edit',
            label: planning ? 'Stop bulk editing' : 'Bulk edit reorder levels',
            hint: planning ? 'back to the catalog' : 'many parts at once',
            onSelect: () => changeMode(planning ? 'catalog' : 'planning'),
          },
        ]
      : []),
    {
      label: 'Export CSV',
      hint: atCap
        ? `the first ${exportCount} rows, current filters`
        : `${exportCount} ${exportCount === 1 ? 'row' : 'rows'}, current filters`,
      disabled: loading || exportCount === 0,
      onSelect: () => {
        // null: the table was still loading and wrote nothing.
        const n = tableRef.current?.exportCsv('parts-catalog');
        if (n == null) {
          toast.error('The list is still loading. Try again in a moment.');
          return;
        }
        toast.success(`Exported ${n} row${n === 1 ? '' : 's'}.`);
      },
    },
  ];

  const groupBy = useMemo(
    () =>
      groupByFamily
        ? {
            key: groupKey,
            renderHeader: (family: string, list: PartsCatalogRow[]) =>
              `${family} · ${list.length}`,
          }
        : undefined,
    [groupByFamily]
  );

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title="Parts Catalog"
        helpTitle="About the parts catalog"
        lede={
          <>
            The master list of every part, one row per item. Stock on hand lives in{' '}
            <Link to="/inventory" className="text-primary hover:underline">Inventory</Link>.
          </>
        }
        help={
          <>
            <p>
              The catalog is the list of every part the company knows about: one row per
              SKU, with its specs, its family and its reorder rules. It is a list of
              IDENTITIES, not quantities. A part can exist here with none of it in stock,
              and that is normal for something you have defined but not yet bought.
            </p>
            <p className="text-on-surface font-medium">SKUs are built, not typed</p>
            <p>
              When you add an item you compose the SKU from its parts rather than typing a
              string, so the format stays consistent. Boards are strict, because the app
              reads a board SKU to work out which family it belongs to. Everything else is
              looser but still follows brand, type, generation, number.
            </p>
            <p className="text-on-surface font-medium">Every item needs two classifications</p>
            <p>
              <span className="text-on-surface">Build stage</span> says where it sits in what
              you make: raw material, part-built, finished, bought-to-resell, or a shop
              supply. <span className="text-on-surface">Item type</span> says what kind of
              thing it is: a board, a cable, a fastener, a battery. They are separate
              questions. A battery pack is raw material AND a battery.
            </p>
            <p>
              Neither is filled in for you, and neither has a default, on purpose. A
              default is a classification nobody made, and that is how an earlier version
              of this catalog ended up with every single row marked the same.
            </p>
            <p className="text-on-surface font-medium">Renaming a SKU</p>
            <p>
              An admin can rename one from the item page. The old code keeps working
              forever: it is kept as a former name, and scanning or typing it still finds
              the item. Nothing on a shelf needs relabeling.
            </p>
            <p className="text-on-surface font-medium">Reorder levels</p>
            <p>
              Choose Bulk edit reorder levels under Tools to set three numbers for many parts in one sitting.{' '}
              <span className="text-on-surface">Reorder at</span> is when stock plus what is
              on order falls to this number; the part then shows up under Below reorder
              threshold on the purchase orders page. <span className="text-on-surface">Safety
              stock</span> is the buffer you want to keep on hand.{' '}
              <span className="text-on-surface">Lead time</span> is the days from ordering to
              the part landing, and blank means nobody has asked the vendor yet. Enter saves
              and moves down the column, Shift+Enter moves up, Escape undoes what you typed.
            </p>
            <p className="text-on-surface font-medium">Deleting</p>
            <p>
              You can only remove an item nothing has ever touched. Once it has been
              ordered, received, counted or flashed, it stays, because deleting it would
              punch a hole in that history.
            </p>
          </>
        }
        primary={
          canMint ? <PrimaryAction onClick={() => setMinting(true)}>+ New item</PrimaryAction> : undefined
        }
        actions={
          <>
            {/* The BULK door beside the single one (039). Quieter than "+ New item"
                on purpose: adding one thing you are looking at is the everyday act,
                while reading a document is the occasional one that saves an hour. */}
            {canMint && <SecondaryAction to="/parts-catalog/import">From a document</SecondaryAction>}
            {/* Families are a property OF the catalog, so they belong on it, and as
                somewhere to go and look rather than somewhere to start work. */}
            {(user.isadmin || user.can_admin) && (
              <SecondaryAction variant="text" to="/admin/part-families">Part families</SecondaryAction>
            )}
            {/* The vocabulary SKUs are spelled from, same gate as minting one. */}
            {canMint && (
              <SecondaryAction variant="text" to="/admin/item-kinds">Item kinds</SecondaryAction>
            )}
          </>
        }
        tools={tools}
      />
      }
    >

      <FilterBar>
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search the catalog by name, SKU, MPN or manufacturer"
          className="flex-1 min-w-[20rem] max-w-md"
          placeholder="Search name, SKU, MPN, manufacturer…"
        />
        <FilterSelect hideLabel label="Filter by family" value={familyId} onChange={setFamilyId}>
          <option value="">All families</option>
          {families.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </FilterSelect>
        {canEdit && (
          <FilterSelect hideLabel label="Filter by item kind" value={kindKey} onChange={setKindKey}>
            <option value="">All kinds</option>
            {kindOptions.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </FilterSelect>
        )}
        {/* Labels visible on these two: their first option is a bare "All", which
            names no axis, so two identical pills would read the same. */}
        <FilterSelect
          label="Consumable"
          value={consumable}
          onChange={(v) => setConsumable(v as '' | 'true' | 'false')}
        >
          <option value="">All</option>
          <option value="true">Consumable</option>
          <option value="false">Non-consumable</option>
        </FilterSelect>
        <FilterSelect
          label="Tracking"
          value={perInstance}
          onChange={(v) => setPerInstance(v as '' | 'true' | 'false')}
        >
          <option value="">All</option>
          <option value="true">Per-instance tracked</option>
          <option value="false">Box-level only</option>
        </FilterSelect>
        <Chip pressed={recentOnly} onClick={() => setRecentOnly(!recentOnly)}>Recent (7d)</Chip>
        <Chip pressed={showRetired} onClick={() => setShowRetired(!showRetired)}>Show retired</Chip>
        <Chip pressed={groupByFamily} onClick={() => setGroupByFamily(!groupByFamily)}>Group by family</Chip>
        <FilterBreak />
        <CommodityClassFilterChips value={commodityFilter} onChange={setCommodityFilter} />
        <MaterialTypeFilterSelect
          id="catalog-material-type"
          value={materialFilter}
          onChange={setMaterialFilter}
        />
      </FilterBar>

      {/* Table. The vessel is the DataTable's — no page wrapper. The SKU count
          rides the working strip, where it names the pile it is counting. */}
      <DataTable<PartsCatalogRow>
        ref={tableRef}
        data={displayRows}
        columns={columns}
        meta={tableMeta}
        getRowId={getRowId}
        sortable
        stickyHeader
        initialSorting={[{ id: 'item', desc: false }]}
        columnVisibility={columnVisibility}
        groupBy={groupBy}
        strip={
          planning ? (
            <span className="flex items-center gap-3 text-xs text-on-surface-variant tabular-nums">
              <span>
                {withLevel} of {displayRows.length} {withLevel === 1 ? 'has' : 'have'} a reorder level
              </span>
              <Progress value={withLevel} max={displayRows.length} label="Items with a reorder level" size="sm" className="w-24" />
            </span>
          ) : (
            <span className="text-xs text-on-surface-variant tabular-nums">{rows.length} SKUs</span>
          )
        }
        loading={loading}
        loadingContent={<LogoSpinner label="Loading the parts catalog" />}
        empty={<EmptyState variant="area">No SKUs match the current filter.</EmptyState>}
      />

      {minting && (
        <NewItemModal
          onClose={() => setMinting(false)}
          onMinted={(row) => {
            setMinting(false);
            handleMinted(row);
          }}
        />
      )}

      {unminting && (
        <UnmintItemDialog
          item={unminting}
          onCancel={() => setUnminting(null)}
          onConfirm={() => unmintRow(unminting)}
        />
      )}
    </PageLayout>
  );
};

export default PartsCatalogPage;
